import { useState } from 'react';
import { useOnMount } from '~/_shared/hooks/useOnMount';
import { useTranslation } from '~/_shared/utils/hooks';
import { useQuery } from '~/_shared/utils/hooks/useQuery';
import { useUserSign } from '../loginForm/useUserSign';
import { useNavigateAfterLogin } from '../useNavigateAfterLogin';
import { getGoogleSsoRedirectUri } from './sso.helpers';
import {
  googleSsoLogin, SsoLoginResponseCode,
} from './sso.repository';
import { SsoPageComponent } from './ssoPage.component';

export const GOOGLE_SECURITY_TOKEN_KEY = 'security_token';
export const GOOGLE_SECURITY_TOKEN_ITEM_KEY = 'google_security_token';
export const GOOGLE_SSO_SCOPES = 'openid https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile';
export const GOOGLE_SSO_PROMPT = 'consent';

export const GoogleSsoPageContainer = () => {
  const [t] = useTranslation();
  const query = useQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const state = query.get('state') || '';
  const code = query.get('code') || '';
  const queryError = query.get('error') || '';

  const { onLoginSuccess } = useUserSign();

  useNavigateAfterLogin();

  useOnMount(() => {
    if (queryError) {
      // the following code is the same for google and ms but kept separate in case of future changes their messages or handling
      let errorMessage = t('sso.login.error.withCode', { code: '#01' });
      if (queryError === 'access_denied') {
        errorMessage = t('sso.error.permissionsNotGranted');
      }

      setError(errorMessage);
      setIsLoading(false);
      return;
    }

    const performLogin = async () => {
      let errorMessage = undefined;

      const securityToken = new URLSearchParams(JSON.parse(decodeURIComponent(state))).get(GOOGLE_SECURITY_TOKEN_KEY) || '';
      const storedSecurityToken = sessionStorage.getItem(GOOGLE_SECURITY_TOKEN_ITEM_KEY);

      if (securityToken !== storedSecurityToken || !securityToken || !storedSecurityToken) {
        errorMessage = t('sso.login.error.withCode', { code: '#02' });
      }
      if (storedSecurityToken) {
        sessionStorage.removeItem(GOOGLE_SECURITY_TOKEN_ITEM_KEY);
      }

      if (!errorMessage) {
        try {
          const loginResult = await googleSsoLogin({
            code,
            redirectUri: getGoogleSsoRedirectUri(),
            state,
            prompt: GOOGLE_SSO_PROMPT,
            scope: GOOGLE_SSO_SCOPES,
          });

          if (loginResult.code === SsoLoginResponseCode.Success) {
            onLoginSuccess(loginResult, { isSsoLogin: true });
          }
          else {
            throw Error();
          }
        }
        catch (e) {
          errorMessage = errorMessage = t('sso.login.error.withCode', { code: '#03' });
        }
      }

      if (errorMessage) {
        setError(errorMessage);
        setIsLoading(false);
        return;
      }
    };

    performLogin();

    // we want this to happen only once on mount

  });

  return (
    <SsoPageComponent
      error={error}
      isLoading={isLoading}
    />
  );
};
